import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/admin/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Users, MoreHorizontal, Phone, Mail, Calendar, Smartphone, Eye } from "lucide-react";
import { useDriverAccess } from "@/hooks/use-driver-access";
import { DriverAccessModal } from "@/components/fleet/DriverAccessModal";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";
import { DriverFormSheet } from "@/components/fleet/DriverFormSheet";
import { useNavigate } from 'react-router-dom';

interface Driver {
  id: string;
  name: string;
  cpf?: string;
  cnh?: string;
  cnh_category?: string;
  cnh_valid_until?: string;
  phone?: string;
  email?: string;
  status: string;
  created_at: string;
}

export default function FleetDrivers() {
  const { profile, hasCompanyRole, activeCompany } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { canCreate, canEdit, canDelete, isViewer } = useFleetPermissions();
  const [isFormSheetOpen, setIsFormSheetOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [activeTab, setActiveTab] = useState('drivers');
  const navigate = useNavigate();

  // Get current user's companies
  const { data: userCompanies } = useQuery({
    queryKey: ["userCompanies"],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_companies")
        .select("company_id, companies(name)")
        .eq("user_id", profile?.id);
      return data || [];
    },
    enabled: !!profile?.id
  });

  const currentCompanyId = userCompanies?.[0]?.company_id;

  // Driver Access Management
  const {
    driversWithAccess,
    isLoading: isLoadingAccess,
    createAccess,
    toggleAccess,
    resetPin,
    isCreating,
    isUpdating
  } = useDriverAccess(currentCompanyId || '');
  
  const [selectedDriverForAccess, setSelectedDriverForAccess] = useState<any>(null);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);

  // Fetch drivers
  const { data: drivers = [], isLoading } = useQuery({
    queryKey: ["fleetDrivers", currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const { data } = await supabase
        .from("fleet_drivers")
        .select("*")
        .eq("company_id", currentCompanyId)
        .order("created_at", { ascending: false });
      return data || [];
    },
    enabled: !!currentCompanyId
  });

  // Create driver mutation
  const createDriverMutation = useMutation({
    mutationFn: async (driverData: Omit<Driver, 'id' | 'created_at'>) => {
      if (!currentCompanyId) throw new Error("No company selected");
      
      // Limpar CPF antes de salvar (remover formatação)
      const cleanCpf = driverData.cpf?.replace(/\D/g, '') || '';
      
      const { data, error } = await supabase
        .from("fleet_drivers")
        .insert({
          ...driverData,
          cpf: cleanCpf,
          company_id: currentCompanyId,
          created_by: profile?.id,
          cnh_valid_until: driverData.cnh_valid_until || null
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["fleetDrivers"] });
      queryClient.invalidateQueries({ queryKey: ["driver-access"] });
      setIsFormSheetOpen(false);
      toast({
        title: "Motorista adicionado com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao cadastrar motorista",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Update driver mutation
  const updateDriverMutation = useMutation({
    mutationFn: async ({ id, ...driverData }: { id: string } & Omit<Driver, 'id' | 'created_at'>) => {
      // Limpar CPF antes de salvar (remover formatação)
      const cleanCpf = driverData.cpf?.replace(/\D/g, '') || '';
      
      const { data, error } = await supabase
        .from("fleet_drivers")
        .update({
          ...driverData,
          cpf: cleanCpf,
          updated_by: profile?.id,
          cnh_valid_until: driverData.cnh_valid_until || null
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["fleetDrivers"] });
      queryClient.invalidateQueries({ queryKey: ["driver-access"] });
      setIsFormSheetOpen(false);
      setEditingDriver(null);
      toast({
        title: "Motorista atualizado com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar motorista",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Delete driver mutation
  const deleteDriverMutation = useMutation({
    mutationFn: async (driverId: string) => {
      // Verificar permissão primeiro
      if (!hasCompanyRole(activeCompany?.id, 'company_admin')) {
        throw new Error('Você não tem permissão para excluir motoristas. Apenas administradores da empresa podem realizar esta ação.');
      }

      const { error, count } = await supabase
        .from("fleet_drivers")
        .delete()
        .eq("id", driverId);

      if (error) throw error;
      
      if (count === 0) {
        throw new Error('Motorista não encontrado ou já foi removido.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetDrivers"] });
      toast({
        title: "Motorista removido com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao remover motorista",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleEdit = (driver: Driver) => {
    setEditingDriver(driver);
    setIsFormSheetOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Ativo</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-800">Inativo</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-800">Suspenso</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const isLicenseExpiring = (dateString: string | null) => {
    if (!dateString) return false;
    try {
      const date = new Date(dateString);
      const today = new Date();
      const diffTime = date.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 30 && diffDays >= 0;
    } catch {
      return false;
    }
  };

  const isLicenseExpired = (dateString: string | null) => {
    if (!dateString) return false;
    try {
      const date = new Date(dateString);
      const today = new Date();
      return date < today;
    } catch {
      return false;
    }
  };

  const columns: ColumnDef<Driver>[] = [
    {
      accessorKey: "name",
      header: "Nome",
      cell: ({ row }) => {
        const driver = row.original;
        return (
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{driver.name}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "cpf",
      header: "CPF",
      cell: ({ row }) => {
        const driver = row.original;
        return driver.cpf ? driver.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '-';
      },
    },
    {
      accessorKey: "cnh",
      header: "CNH",
      cell: ({ row }) => {
        const driver = row.original;
        return (
          <div>
            <div className="font-medium">{driver.cnh || '-'}</div>
            {driver.cnh_category && (
              <div className="text-sm text-muted-foreground">Cat. {driver.cnh_category}</div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "cnh_valid_until",
      header: "Vencimento CNH",
      cell: ({ row }) => {
        const driver = row.original;
        const isExpiring = isLicenseExpiring(driver.cnh_valid_until);
        const isExpired = isLicenseExpired(driver.cnh_valid_until);
        
        return (
          <div className="flex items-center gap-2">
            <span className={`font-medium ${
              isExpired ? 'text-red-600' : isExpiring ? 'text-orange-600' : ''
            }`}>
              {formatDate(driver.cnh_valid_until)}
            </span>
            {(isExpiring || isExpired) && (
              <Calendar className={`w-3 h-3 ${
                isExpired ? 'text-red-600' : 'text-orange-600'
              }`} />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "phone",
      header: "Contato",
      cell: ({ row }) => {
        const driver = row.original;
        return (
          <div className="space-y-1">
            {driver.phone && (
              <div className="flex items-center gap-1 text-sm">
                <Phone className="w-3 h-3" />
                {driver.phone}
              </div>
            )}
            {driver.email && (
              <div className="flex items-center gap-1 text-sm">
                <Mail className="w-3 h-3" />
                {driver.email}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => getStatusBadge(row.getValue("status")),
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => {
        const driver = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={() => navigate(`/company/${activeCompany?.slug}/admin/systems/fleet/drivers/${driver.id}`)}
              >
                <Eye className="mr-2 h-4 w-4" />
                Ver Detalhes
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleEdit(driver)}
                disabled={!canEdit}
              >
                <Edit className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
              {canDelete && (
                <DropdownMenuItem 
                  onClick={() => deleteDriverMutation.mutate(driver.id)}
                  className="text-red-600"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remover
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  if (!currentCompanyId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-lg font-semibold mb-2">Nenhuma empresa encontrada</h2>
          <p className="text-muted-foreground">
            Você precisa estar associado a uma empresa para gerenciar motoristas.
          </p>
        </div>
      </div>
    );
  }

  const activeDrivers = drivers.filter(d => d.status === 'active');
  const inactiveDrivers = drivers.filter(d => d.status === 'inactive');
  const expiredLicenses = drivers.filter(d => isLicenseExpired(d.cnh_valid_until));
  const expiringLicenses = drivers.filter(d => isLicenseExpiring(d.cnh_valid_until) && !isLicenseExpired(d.cnh_valid_until));

  const driversWithMobileAccess = driversWithAccess.filter(d => d.has_access);

  const handleOpenAccessModal = (driver: any) => {
    setSelectedDriverForAccess(driver);
    setIsAccessModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Driver Access Modal */}
      <DriverAccessModal
        open={isAccessModalOpen}
        onOpenChange={setIsAccessModalOpen}
        driver={selectedDriverForAccess}
        onSave={(driverId, pinCode) => createAccess({ driverId, pinCode })}
        onToggle={(driverId, isActive) => toggleAccess({ driverId, isActive })}
        onReset={(driverId, newPin) => resetPin({ driverId, newPin })}
        isLoading={isCreating || isUpdating}
      />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Motoristas</h1>
          <p className="text-muted-foreground">
            Gerencie a equipe de motoristas da sua frota
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {isViewer && (
            <Badge variant="secondary">
              👁️ Modo Visualização
            </Badge>
          )}
          <Button 
            onClick={() => {
              setEditingDriver(null);
              setIsFormSheetOpen(true);
            }} 
            disabled={!canCreate}
          >
            <Plus className="w-4 h-4 mr-2" />
            Cadastrar Motorista
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Motoristas</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{drivers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ativos</CardTitle>
            <div className="h-2 w-2 rounded-full bg-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeDrivers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">CNH Vencendo</CardTitle>
            <Calendar className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{expiringLicenses.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">CNH Vencidas</CardTitle>
            <Calendar className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{expiredLicenses.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="drivers">
            <Users className="w-4 h-4 mr-2" />
            Motoristas
          </TabsTrigger>
          <TabsTrigger value="access">
            <Smartphone className="w-4 h-4 mr-2" />
            Acesso Mobile
          </TabsTrigger>
        </TabsList>

        {/* Drivers Table Tab */}
        <TabsContent value="drivers" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Lista de Motoristas</CardTitle>
              <CardDescription>
                Gerencie todos os motoristas da sua equipe
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={columns}
                data={drivers}
                searchKey="name"
                searchPlaceholder="Buscar por nome..."
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mobile Access Tab */}
        <TabsContent value="access" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Acesso Mobile dos Motoristas</CardTitle>
              <CardDescription>
                Configure e gerencie o acesso dos motoristas ao aplicativo mobile
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid gap-4 md:grid-cols-3 mb-6">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                      <Users className="w-4 h-4" />
                      Motoristas Ativos
                    </div>
                    <div className="text-2xl font-bold">{activeDrivers.length}</div>
                  </div>
                  <div className="p-4 bg-[#00BFA5]/10 rounded-lg border border-[#00BFA5]/20">
                    <div className="flex items-center gap-2 text-sm text-[#00BFA5] mb-1">
                      <Smartphone className="w-4 h-4" />
                      Com Acesso Mobile
                    </div>
                    <div className="text-2xl font-bold text-[#00BFA5]">{driversWithMobileAccess.length}</div>
                  </div>
                  <div className="p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-900">
                    <div className="flex items-center gap-2 text-sm text-orange-700 dark:text-orange-300 mb-1">
                      <Smartphone className="w-4 h-4" />
                      Sem Acesso Configurado
                    </div>
                    <div className="text-2xl font-bold text-orange-700 dark:text-orange-300">
                      {activeDrivers.length - driversWithMobileAccess.length}
                    </div>
                  </div>
                </div>

                {/* Drivers List */}
                {isLoadingAccess ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Carregando...
                  </div>
                ) : driversWithAccess.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhum motorista ativo encontrado
                  </div>
                ) : (
                  <div className="space-y-2">
                    {driversWithAccess.map((driver) => (
                      <div
                        key={driver.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Smartphone className={`w-4 h-4 ${driver.has_access ? 'text-[#00BFA5]' : 'text-muted-foreground'}`} />
                              <span className="font-medium">{driver.name}</span>
                            </div>
                            {driver.has_access && (
                              <Badge variant={driver.is_active ? 'default' : 'secondary'} className="text-xs">
                                {driver.is_active ? 'Ativo' : 'Inativo'}
                              </Badge>
                            )}
                            {!driver.has_access && (
                              <Badge variant="outline" className="text-xs">
                                Não configurado
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span>CPF: {driver.cpf ? driver.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '-'}</span>
                            {driver.last_login_at && (
                              <span className="text-xs">
                                Último acesso: {format(new Date(driver.last_login_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant={driver.has_access ? "outline" : "default"}
                          size="sm"
                          onClick={() => handleOpenAccessModal(driver)}
                          disabled={!canEdit}
                        >
                          {driver.has_access ? 'Gerenciar' : 'Configurar Acesso'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Driver Form Sheet */}
      <DriverFormSheet
        open={isFormSheetOpen}
        onOpenChange={(open) => {
          setIsFormSheetOpen(open);
          if (!open) {
            setEditingDriver(null);
          }
        }}
        companyId={currentCompanyId || ''}
        mode={editingDriver ? 'edit' : 'create'}
        initialData={editingDriver}
      />
    </div>
  );
}